//@flow
import React from 'react';
import * as Immutable from 'immutable';
import Dialog from 'common/components/core/dialog';
import { SingleTeamSelect } from 'common/entity/components/team';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { withRouter } from 'common/components/core/router';
import { compose } from 'recompose';
import { translate } from 'react-i18next';

import theme from './CreateBatchModal.scss';
import HandleOutsideClick from 'common/components/core/util/HandleOutsideClick';

import Switch from 'common/components/core/switch';
import DateTimePicker from 'common/components/core/form/DateTimePicker';
import ButtonGroup from 'common/components/core/button/ButtonGroup';
import Input from 'common/components/core/input';
import { SelectTypes } from 'common/components/core/select';
import { Homebase } from 'common/models/internal/Homebase';
import type { Team } from 'common/models/internal/Team';
import type { List } from 'immutable';
import type { TFunction } from 'react-i18next';
import {
    HomebaseSelector,
    MultiHomebaseSelect
} from 'common/components/core/autocomplete/HomebaseSelector';
import { homebase as fetchHomebasesApi } from 'common/middleware/api';
import { Loader } from 'common/components/core/loader/Loader';
import { SizeEnum } from 'common/enums/UIEnums';
import entityIds from 'common/tests/entity/entityIds.json';
import DataUtils from 'common/utils/DataUtils';
import TimeUtils from 'common/utils/TimeUtils';

import { TeamSettings } from 'common/models/internal/TeamSettings';
import { TaskProfile } from 'common/models/internal/TaskProfile';
import { getDispatcherVisibilitySettings } from 'common/redux/selectors/clientSelector';
import {
    getTeamSettingsForBatchUpload,
    isTeamSettingsForBatchUploadLoading,
    getTaskProfileForBatchUpload,
    isTaskProfileForBatchUploadLoading,
    getTaskProfileBatchUploadError,
    getTeamSettingsBatchUploadError
} from 'common/redux/selectors/teamSettingsSelector';
import {
    fetchTeamSettingsById,
    fetchTaskProfile
} from 'common/redux/actions/team';
import clientSettingsSelector from 'common/redux/selectors/clientSettingsSelector';
import {
    getTeamPreferences,
    getIsTeamPreferencesLoading
} from 'common/redux/selectors/teamPreferencesSelector';
import { personnelActiveTeamValuesSelector } from 'common/redux/selectors/personnelTeamsSelector';
import { fetchHomebasesByClientId } from 'common/redux/actions/homebase';
import TeamPreferencesActions from 'common/redux/actions/teamPreferences';

import { Text } from '@locus-taxy/neo-ui';
import { validateId } from 'common/utils/ValidationUtil';
import { DateGroupEnum } from 'common/enums/DateTimeEnums';
import DateTimeUtils, { CustomDateFormats } from 'common/utils/DateTimeUtils';

const EnhancedDateTimePicker = HandleOutsideClick(DateTimePicker);

const Label = ({ theme, title, subtitle }) => (
    <div className={theme.buttonLabel}>
        <div className={theme.title}>{title}</div>
        <div className={theme.subtitle}>{subtitle}</div>
    </div>
);

type Props = {
    clientId: string,
    isCreateBatchInProgress: boolean,
    personnelActiveTeams: List<Team>,
    onCreate: ({
        batchDate: string,
        batchId: string,
        teamId: string,
        homebaseId: string,
        selectedHomebaseOptions: array<string>,
        crossDockEnabled: boolean
    }) => void,
    onCancel: () => void,
    t: TFunction,
    taskProfile: ?TaskProfile,
    teamSettings: ?TeamSettings,
    isTeamPreferencesLoading: boolean,
    isTeamSettingsLoading: boolean,
    isTaskProfileLoading: boolean,
    fetchTeamSettingsById: (teamId: string, cliendId: string) => void,
    fetchTaskProfile: (
        teamId: string,
        clientId: string,
        activeProfileId: string
    ) => void,
    fetchTeamPreferences: (teamId: string, clientId: string) => void,
    isCrossDockClient: boolean,
    taskProfileHasError: boolean,
    teamSettingsHasError: boolean,
    isNoRowSelected: boolean,
    totalOrders: number,
    errorIds: Array<string>,
    validateOrderIds: (Array<string>) => void
};
type State = {
    teamId: ?string,
    homebaseId: ?string,
    isFetchingHomebases: boolean,
    homebases: List<Homebase>,
    selectedHomebaseOptions: ?List<Homebase>,
    batchId: ?string,
    batchDate: ?string,
    datePickerVisible: boolean,
    crossDockEnabled: boolean,
    selectedButton: string,
    commaSeparatedIds: string,
    orderIdsCount: number,
    batchIdErrorMessage: ?string
};

class CreateBatchModal extends React.PureComponent<Props, State> {
    state = {
        homebaseId: null,
        teamId: null,
        isFetchingHomebases: false,
        homebases: Immutable.List(),
        selectedHomebaseOptions: null,
        batchId: DateTimeUtils.customFormat(
            TimeUtils.now(),
            CustomDateFormats.INTERNAL_BATCH_DATE
        ),
        datePickerVisible: false,
        crossDockEnabled: false,
        batchDate: moment(),
        selectedButton: DateGroupEnum.Today,
        commaSeparatedIds: '',
        orderIdsCount: 0,
        batchIdErrorMessage: null
    };

    componentDidUpdate(prevProps) {
        const { clientId, fetchTaskProfile, teamSettings } = this.props;
        const { teamSettings: prevTeamSettings } = prevProps;
        if (teamSettings) {
            const { teamId } = teamSettings;
            const { teamId: prevTeamId } = prevTeamSettings || {};
            if (teamId !== prevTeamId) {
                fetchTaskProfile(
                    teamId,
                    clientId,
                    teamSettings.activeTaskProfileId
                );
            }
        }
    }

    handleTeamSelect = ({ value }) => {
        this.setState(
            {
                teamId: value,
                homebaseId: null,
                selectedHomebaseOptions: []
            },
            this.fetchHomebases,
            this.fetchTeamSettings(this.props.clientId, value)
        );
    };

    handleHomebaseSelect = ({ value }) => {
        this.setState({
            homebaseId: value
        });
    };

    handleHomebaseOptionsSelect = (
        selectedHomebaseOptions: Array<HomebaseOption>
    ) => {
        this.setState({
            selectedHomebaseOptions
        });
    };

    handleBatchIdChange = value => {
        const batchIdErrorMessage = validateId(value);
        this.setState({
            batchId: value,
            batchIdErrorMessage
        });
    };

    handleAccept = () => {
        const {
            batchDate,
            batchId,
            teamId,
            homebaseId,
            selectedHomebaseOptions,
            crossDockEnabled,
            commaSeparatedIds
        } = this.state;
        const batchDateValue = TimeUtils.formatWithEnglishLocale(batchDate);
        const { onCreate } = this.props;
        const homebaseOptions = selectedHomebaseOptions.map(
            option => option.value
        );
        onCreate(
            batchDateValue,
            batchId,
            teamId,
            homebaseId,
            homebaseOptions,
            crossDockEnabled,
            commaSeparatedIds
        );
    };

    handleCancel = () => {
        const { onCancel } = this.props;
        onCancel();
    };

    fetchHomebases = () => {
        const { clientId } = this.props;
        const { teamId: team } = this.state;
        this.setState({
            isFetchingHomebases: true,
            homebases: Immutable.List()
        });
        fetchHomebasesApi.fetch({ clientId, team }).then(response => {
            const homebases = Immutable.List(
                response.map(homebase => Homebase.fromJS(homebase))
            );
            this.setState({ homebases, isFetchingHomebases: false });
        });
    };

    fetchTeamSettings(clientId: string, teamId: string) {
        const {
            fetchTeamSettingsById,
            fetchTeamPreferences,
            isCrossDockClient
        } = this.props;
        fetchTeamSettingsById(teamId, clientId);
        if (isCrossDockClient) {
            fetchTeamPreferences(teamId, clientId);
        }
    }

    getDateGroupValues = () => {
        const { t } = this.props;
        const { batchDate, selectedButton } = this.state;
        return DateGroupEnum.values().map(value => {
            let subtitle = t('chooseSlot');
            if (value === DateGroupEnum.Today) {
                subtitle = TimeUtils.format(moment(), "Do MMM [']YY");
            } else if (value === DateGroupEnum.Tomorrow) {
                subtitle = TimeUtils.format(
                    moment().add(1, 'days'),
                    "Do MMM [']YY"
                );
            } else if (
                value === DateGroupEnum.Custom &&
                selectedButton === DateGroupEnum.Custom &&
                batchDate
            ) {
                subtitle = TimeUtils.format(batchDate, "Do MMM [']YY");
            }
            return {
                label: Label({
                    theme,
                    title: t(value.toLowerCase()),
                    subtitle
                }),
                value,
                testId: `${value.toLowerCase()}Button`
            };
        });
    };

    handleDateButtonClick = values => {
        const [{ value }] = values;
        this.setState({
            selectedButton: value
        });
        switch (value) {
            case DateGroupEnum.Custom:
                this.toggleDatePicker();
                break;
            case DateGroupEnum.Today:
                this.handleDateChange(moment());
                break;
            case DateGroupEnum.Tomorrow:
                this.handleDateChange(moment().add(1, 'days'));
                break;
        }
    };

    toggleDatePicker = () => {
        if (this.state.datePickerVisible) {
            this.hideDatePicker();
        } else {
            this.showDatePicker();
        }
    };

    handleOutsideClick = () => {
        this.hideDatePicker();
    };

    handleDateChange = (value, willBeVisible, shouldClose) => {
        const { datePickerVisible } = this.state;

        this.setState({
            batchDate: value
        });
        if (datePickerVisible && shouldClose) {
            this.hideDatePicker();
        }
    };

    toggleCrossdock = value => {
        this.setState({
            crossDockEnabled: value
        });
    };

    hideDatePicker = () => {
        this.setState({ datePickerVisible: false });
    };

    showDatePicker = () => {
        this.setState({ datePickerVisible: true });
    };

    isHomebaseRequired = taskProfile => {
        return (
            taskProfile.csvParserSettings.useCase === 'DELIVERY_FROM_HOMEBASE'
        );
    };

    isHomebaseSelected = () => {
        const {
            isTeamPreferencesLoading,
            isTeamSettingsLoading,
            isTaskProfileLoading,
            isFetchingHomebases
        } = this.props;
        if (
            isTeamPreferencesLoading ||
            isTeamSettingsLoading ||
            isTaskProfileLoading ||
            isFetchingHomebases
        ) {
            return false;
        }
        return !!this.state.homebaseId;
    };

    isAddBatchButtonEnabled = () => {
        const {
            batchDate,
            teamId,
            commaSeparatedIds,
            batchIdErrorMessage
        } = this.state;
        const {
            taskProfile,
            isTeamPreferencesLoading,
            isTeamSettingsLoading,
            isTaskProfileLoading,
            taskProfileHasError,
            teamSettingsHasError,
            isNoRowSelected,
            errorIds
        } = this.props;
        const isHomebaseRequired =
            taskProfile && this.isHomebaseRequired(taskProfile);
        const isFetchingTeamInfo =
            isTeamPreferencesLoading ||
            isTeamSettingsLoading ||
            isTaskProfileLoading;
        const homebaseEnabled = isHomebaseRequired && this.isHomebaseSelected();

        if (
            !batchDate ||
            !teamId ||
            !taskProfile ||
            isFetchingTeamInfo ||
            taskProfileHasError ||
            teamSettingsHasError ||
            batchIdErrorMessage
        ) {
            return false;
        }
        if (isHomebaseRequired && !homebaseEnabled) {
            return false;
        }
        if (
            isNoRowSelected &&
            (DataUtils.isEmpty(commaSeparatedIds) || errorIds.length > 0)
        ) {
            return false;
        }
        return true;
    };

    setCommaSeparatedIds = e => {
        this.setState({ commaSeparatedIds: e.target.value });
    };

    handleBlur = () => {
        const { validateOrderIds } = this.props;
        const { commaSeparatedIds } = this.state;
        let orderIdsCount = 0;
        let orderIds = [];
        if (!DataUtils.isEmpty(commaSeparatedIds)) {
            orderIds = commaSeparatedIds
                .split(',')
                .map(id => id.trim())
                .filter(id => id);
            orderIdsCount = orderIds.length;
        }
        validateOrderIds(orderIds);
        this.setState({ orderIdsCount });
    };

    formatOrderCount = (orderCount, t) => {
        if (orderCount > 1) {
            return `${orderCount} ${t(`entities:order_plural`)}`;
        } else {
            return `${orderCount} ${t(`entities:order`)}`;
        }
    };

    render() {
        const {
            personnelActiveTeams,
            onCancel,
            t,
            isCreateBatchInProgress,
            showCrossDockFields,
            isTaskProfileLoading,
            isTeamSettingsLoading,
            isTeamPreferencesLoading,
            taskProfile,
            allowHomebaseOptions,
            teamSettingsHasError,
            taskProfileHasError,
            isNoRowSelected,
            totalOrders,
            errorIds
        } = this.props;
        const {
            homebaseId,
            teamId,
            isFetchingHomebases,
            homebases,
            selectedHomebaseOptions,
            batchId,
            datePickerVisible,
            batchDate,
            crossDockEnabled,
            commaSeparatedIds,
            orderIdsCount,
            batchIdErrorMessage
        } = this.state;
        const selectedHomebase =
            homebases && homebases.find(homebase => homebase.id === homebaseId);
        const selectedHomebaseAddress =
            (selectedHomebase && selectedHomebase.getAddressString()) || null;
        const isHomebaseRequired =
            !isTaskProfileLoading &&
            !isTeamSettingsLoading &&
            !isTeamPreferencesLoading &&
            !taskProfileHasError &&
            !teamSettingsHasError &&
            taskProfile &&
            this.isHomebaseRequired(taskProfile);
        const isHomebaseVisible =
            !DataUtils.isEmpty(teamId) &&
            !homebases.isEmpty() &&
            isHomebaseRequired;

        const minDate = moment();
        const showCrossDockSwitcher =
            !DataUtils.isEmpty(teamId) &&
            !isTeamSettingsLoading &&
            !isTeamPreferencesLoading &&
            showCrossDockFields;

        const isCreateEnabled = this.isAddBatchButtonEnabled();
        const subtitle = isNoRowSelected
            ? orderIdsCount > 0
                ? this.formatOrderCount(orderIdsCount, t)
                : null
            : this.formatOrderCount(totalOrders, t);
        return (
            <Dialog
                title={t('planOnDispatchIQ')}
                subtitle={subtitle}
                theme={theme}
                active
                disableAccept={!isCreateEnabled}
                acceptActionLabel={t('planOnDispatchIQ')}
                onAccept={this.handleAccept}
                onCancel={onCancel}
                onDismiss={onCancel}
            >
                {isFetchingHomebases ||
                isCreateBatchInProgress ||
                isTaskProfileLoading ||
                isTeamPreferencesLoading ||
                isTeamSettingsLoading ? (
                    <Loader active theme={theme} />
                ) : null}
                <div className={theme.selectorsWrap}>
                    <ButtonGroup
                        theme={theme}
                        values={this.getDateGroupValues()}
                        defaultSelected={DateGroupEnum.Today}
                        onChange={this.handleDateButtonClick}
                    >
                        <EnhancedDateTimePicker
                            handleClickOutside={this.handleOutsideClick}
                            theme={theme}
                            controlDateVisibility
                            min={minDate}
                            onSelect={this.handleDateChange}
                            dateVisible={datePickerVisible}
                            value={batchDate}
                            showTime={false}
                        />
                    </ButtonGroup>
                    {isNoRowSelected ? (
                        <>
                            <textarea
                                spellCheck="false"
                                type="text"
                                value={commaSeparatedIds}
                                onChange={this.setCommaSeparatedIds}
                                onBlur={this.handleBlur}
                                className={theme.orderIdInput}
                                placeholder={t(
                                    'masterOrderProperties.orderIdInput'
                                )}
                            />
                            {errorIds.length > 0 && (
                                <Text
                                    color="red.500"
                                    fontSize={1}
                                    width="395px"
                                    mb={2}
                                >
                                    {t('masterOrderProperties.invalidOrder', {
                                        orders: errorIds.join(', '),
                                        count: errorIds.length
                                    })}
                                </Text>
                            )}
                        </>
                    ) : null}
                    <Input
                        autoFocus
                        tabIndex={1}
                        theme={theme}
                        placeholder={t('batchId')}
                        value={batchId}
                        onChange={this.handleBatchIdChange}
                        className={theme.batchId}
                    />
                    {batchIdErrorMessage && (
                        <Text
                            color="red.500"
                            fontSize={1}
                            width="395px"
                            mb={4}
                            mt={-3}
                        >
                            {batchIdErrorMessage}
                        </Text>
                    )}
                    <SingleTeamSelect
                        testId={entityIds.selectTeam}
                        selectSize={SizeEnum.Large}
                        teams={personnelActiveTeams}
                        placeholder={t('translations:selectTeam')}
                        theme={theme}
                        isClearable={false}
                        onChange={this.handleTeamSelect}
                        value={teamId}
                        className={theme.teamSelector}
                    />
                    {isHomebaseVisible ? (
                        <HomebaseSelector
                            testId={entityIds.selectHomeBase}
                            showHomebaseIcon
                            selectSize={SizeEnum.Large}
                            onChange={this.handleHomebaseSelect}
                            selectedAddress={selectedHomebaseAddress}
                            homebases={homebases}
                            isClearable={false}
                            placeholder={t('translations:selectHomebase')}
                            theme={theme}
                            selectType={SelectTypes.Raised}
                            className={theme.homebaseSelector}
                        />
                    ) : null}

                    {isHomebaseVisible && allowHomebaseOptions ? (
                        <MultiHomebaseSelect
                            selectSize={SizeEnum.Large}
                            isMulti
                            onChange={this.handleHomebaseOptionsSelect}
                            homebases={homebases}
                            selectedHomebases={selectedHomebaseOptions}
                            placeholder={t('selectHomebaseOptions')}
                            theme={theme}
                            selectType={SelectTypes.Raised}
                            className={theme.homebasesSelector}
                        />
                    ) : null}
                    {showCrossDockSwitcher ? (
                        <div className={theme.crossDockContainer}>
                            <div>{t('entities:crossDock')}</div>
                            <Switch
                                checked={crossDockEnabled}
                                onChange={this.toggleCrossdock}
                            />
                        </div>
                    ) : null}
                </div>
            </Dialog>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const personnelActiveTeams = personnelActiveTeamValuesSelector(
        state,
        ownProps
    );
    const isTeamSettingsLoading = isTeamSettingsForBatchUploadLoading(
        state,
        ownProps
    );
    const teamSettings = getTeamSettingsForBatchUpload(state, ownProps);
    const isTaskProfileLoading = isTaskProfileForBatchUploadLoading(
        state,
        ownProps
    );
    const taskProfileHasError = getTaskProfileBatchUploadError(state, ownProps);
    const teamSettingsHasError = getTeamSettingsBatchUploadError(
        state,
        ownProps
    );
    const taskProfile = getTaskProfileForBatchUpload(state, ownProps);
    const clientSettings = clientSettingsSelector(state, ownProps);
    const allowHomebaseOptions =
        clientSettings && clientSettings.allowHomebaseOptions;
    const dispatcherVisibilitySettings = getDispatcherVisibilitySettings(
        state,
        ownProps
    );
    const isCrossDockClient = dispatcherVisibilitySettings?.showCrossDockFields;
    const teamPreferences = getTeamPreferences(state, ownProps);
    const isTeamPreferencesLoading =
        isCrossDockClient && getIsTeamPreferencesLoading(state, ownProps);
    const showCrossDockFields =
        isCrossDockClient &&
        teamPreferences?.dispatcherVisibilitySettings.showCrossDockFields;
    return {
        personnelActiveTeams,
        taskProfile,
        teamSettings,
        isTeamSettingsLoading,
        isTaskProfileLoading,
        taskProfileHasError,
        teamSettingsHasError,
        allowHomebaseOptions,
        showCrossDockFields,
        isTeamPreferencesLoading,
        isCrossDockClient
    };
};

const withRedux = connect(mapStateToProps, {
    fetchTeamSettingsById,
    fetchTaskProfile,
    fetchHomebasesByClientId,
    fetchTeamPreferences: TeamPreferencesActions.fetchTeamPreferences
});

export default compose(
    withRouter,
    withRedux,
    translate('entityManagement')
)(CreateBatchModal);
